import {
    Mutations,
    MutationTypes,
    State,
    TodoListApiData,
    TodoListItemApiData,
    TodoListsApiData
} from '../types/todo-lists.types';
import { TodoList } from '../../../scripts/api/models/todoList';
import { TodoListItem } from '../../../scripts/api/models/todoListItem';
import { TodoListMember } from '../../../scripts/api/models/todoListMember';
import { STATES } from './state';

const mutations: & Mutations = {
    [MutationTypes.SET_CURRENT_TODO_LIST] (state: State, todoList: TodoList): void {
        state.currentTodoList = todoList;

        state.currentTodoListItems = Array.from(state.currentTodoList.items.values());
    },
    [MutationTypes.TODO_LISTS_FETCHING] (state: State): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHING;
    },
    [MutationTypes.TODO_LISTS_FETCHED] (state: State, data: TodoListsApiData | undefined): void {
        if (data === undefined) {
            state.todoListsState = STATES.TODO_LISTS_INIT;
            return;
        }

        state.todoListsState = STATES.TODO_LISTS_FETCHED;
        const todoLists = new Map();
        data.lists.forEach((list: TodoList) => {
            const itemMap = new Map();
            list.items.forEach(item => {
                itemMap.set(item.uid, item);
            });
            list.items = itemMap;

            const memberMap = new Map();
            list.members.forEach(member => {
                memberMap.set(member.uid, member);
            });
            list.members = memberMap;

            todoLists.set(list.uid, list);
        });
        state.todoLists = todoLists;
        state.currentTodoList = todoLists.values().next().value;
        state.currentTodoListItems = state.currentTodoList !== null ? Array.from(state.currentTodoList.items.values()) : [];
    },
    [MutationTypes.TODO_LIST_ADDING] (state: State): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHING;
    },
    [MutationTypes.TODO_LIST_ADDED] (state: State, data: TodoListApiData | undefined): void {
        if (data === undefined) {
            state.todoListsState = STATES.TODO_LISTS_INIT;
            return;
        }

        state.todoListsState = STATES.TODO_LISTS_FETCHED;

        data.items = new Map();
        data.members = new Map();

        state.todoLists.set(data.uid, data);
        state.currentTodoList = data;
        state.currentTodoListItems = Array.from(state.currentTodoList.items.values());
    },
    [MutationTypes.TODO_LIST_NEW_LIST_SAVING] (state: State): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHING;
    },
    [MutationTypes.TODO_LIST_REMOVING] (state: State): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHING;
    },
    [MutationTypes.TODO_LIST_REMOVED] (state: State, todoList: TodoList): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHED;
        if (state.currentTodoList === null) {
            return;
        }

        state.todoLists.delete(todoList.uid);

        state.currentTodoList = state.todoLists.values().next().value;

        state.currentTodoListItems = state.currentTodoList !== null ? Array.from(state.currentTodoList.items.values()) : [];
    },
    [MutationTypes.TODO_LIST_UPDATING] (state: State): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHING;
    },
    [MutationTypes.TODO_LIST_UPDATED] (state: State, todoList: TodoList): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHED;

        state.todoLists.set(todoList.uid, todoList);
        state.currentTodoList = todoList;
        state.currentTodoListItems = Array.from(state.currentTodoList.items.values());
    },
    [MutationTypes.TODO_LIST_ITEM_ADDING] (state: State): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHING;
    },
    [MutationTypes.TODO_LIST_ITEM_ADDED] (state: State, data: TodoListItemApiData | undefined): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHED;
        if (data === undefined || state.currentTodoList === null) {
            return;
        }

        state.currentTodoList.items.set(data.uid, data);

        state.todoLists.set(state.currentTodoList.uid, state.currentTodoList);

        state.currentTodoListItems = Array.from(state.currentTodoList.items.values());
    },
    [MutationTypes.TODO_LIST_NEW_LIST_ITEM_SAVING] (state: State): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHING;
    },
    [MutationTypes.TODO_LIST_ITEM_REMOVING] (state: State): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHING;
    },
    [MutationTypes.TODO_LIST_ITEM_REMOVED] (state: State, todoListItem: TodoListItem): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHED;
        if (state.currentTodoList === null) {
            return;
        }

        state.currentTodoList.items.delete(todoListItem.uid);

        state.todoLists.set(state.currentTodoList.uid, state.currentTodoList);

        state.currentTodoListItems = Array.from(state.currentTodoList.items.values());
    },
    [MutationTypes.TODO_LIST_ITEM_UPDATED] (state: State, data: TodoListItem): void {
        if (data === undefined || state.currentTodoList === null) {
            return;
        }

        state.currentTodoList.items.set(data.uid, data);

        state.todoLists.set(state.currentTodoList.uid, state.currentTodoList);

        state.currentTodoListItems = Array.from(state.currentTodoList.items.values());
    },
    [MutationTypes.TODO_LIST_MEMBER_ADDING] (state: State): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHING;
    },
    [MutationTypes.TODO_LIST_MEMBER_ADDED] (state: State, payload: { todoList: TodoList, member: TodoListMember }): void {
        const { todoList, member } = payload;

        const currentTodolist = state.todoLists.get(todoList.uid);

        if (currentTodolist) {
            currentTodolist.members.set(member.uid, member);
            state.todoLists.set(currentTodolist.uid, currentTodolist);
        }

        state.todoListsState = STATES.TODO_LISTS_FETCHED;
    },
    [MutationTypes.TODO_LIST_MEMBER_REMOVING] (state: State): void {
        state.todoListsState = STATES.TODO_LISTS_FETCHING;
    },
    [MutationTypes.TODO_LIST_MEMBER_REMOVED] (state: State, payload: { todoList: TodoList, member: TodoListMember | null }): void {
        const { todoList, member } = payload;

        const currentTodolist = state.todoLists.get(todoList.uid);

        if (currentTodolist) {
            if (member) {
                currentTodolist.members.delete(member.uid);
                state.todoLists.set(currentTodolist.uid, currentTodolist);
            } else {
                state.todoLists.delete(currentTodolist.uid);
                state.currentTodoList = null;
                state.currentTodoListItems = [];
            }
        }

        state.todoListsState = STATES.TODO_LISTS_FETCHED;
    },
};

export default mutations;
